import React from 'react';
import type { Profile } from '../../types';
import type { MembroEquipe } from '../../src/lib/projetos/equipe';

interface EquipeAvataresProps {
  equipe: MembroEquipe[];
  users: Pick<Profile, 'id' | 'full_name' | 'avatar_url'>[];
  /** Acima disso vira "+N" — o card tem largura curta e a pilha não pode empurrar o prazo. */
  max?: number;
  size?: number;
}

const primeiroNome = (nome?: string | null) => (nome || '').trim().split(/\s+/)[0] || 'Sem nome';

/**
 * Pilha de avatares da equipe do projeto. Sobrepostos e na ordem da equipe (gestor primeiro),
 * para caber no rodapé do card sem virar uma lista de nomes.
 */
const EquipeAvatares: React.FC<EquipeAvataresProps> = ({ equipe, users, max = 4, size = 24 }) => {
  if (equipe.length === 0) {
    return <span className="text-gray-400 dark:text-gray-500">Sem responsável</span>;
  }

  const perfil = (uid: string) => users.find(u => u.id === uid);
  const visiveis = equipe.slice(0, max);
  const restantes = equipe.length - visiveis.length;
  // O title da pilha lista todo mundo, inclusive quem ficou no "+N".
  const titulo = equipe
    .map(m => `${perfil(m.userId)?.full_name || 'Usuário removido'}${m.papel === 'gestor' ? ' (gestor)' : ''}`)
    .join('\n');

  return (
    <div className="flex items-center gap-2 min-w-0" title={titulo}>
      <div className="flex -space-x-1.5 shrink-0">
        {visiveis.map(m => {
          const u = perfil(m.userId);
          const nome = u?.full_name || '?';
          return u?.avatar_url ? (
            <img key={m.userId} src={u.avatar_url} alt={nome}
              className="rounded-full object-cover ring-2 ring-white dark:ring-gray-800"
              style={{ width: size, height: size }} />
          ) : (
            <div key={m.userId}
              className="rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center font-bold text-gray-600 dark:text-gray-200 ring-2 ring-white dark:ring-gray-800"
              style={{ width: size, height: size, fontSize: size * 0.42 }}>
              {nome.charAt(0).toUpperCase()}
            </div>
          );
        })}
        {restantes > 0 && (
          <div className="rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center font-bold text-gray-500 dark:text-gray-300 ring-2 ring-white dark:ring-gray-800"
            style={{ width: size, height: size, fontSize: size * 0.38 }}>
            +{restantes}
          </div>
        )}
      </div>
      <span className="truncate text-gray-500 dark:text-gray-400">
        {equipe.length === 1
          ? perfil(equipe[0].userId)?.full_name || 'Usuário removido'
          : `${primeiroNome(perfil(equipe[0].userId)?.full_name)} +${equipe.length - 1}`}
      </span>
    </div>
  );
};

export default EquipeAvatares;
